import {
  useLoaderData,
  useLocation,
  useNavigation,
  useOutlet,
  useActionData,
  useNavigate,
  useSubmit,
  Form,
  useRouteLoaderData,
  useAsyncValue
} from 'react-router-dom';

import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  CubeTransparentIcon,
  CalendarIcon,
  BriefcaseIcon,
  PencilIcon,
  UserIcon,
  CurrencyDollarIcon,
  DocumentPlusIcon,
  EyeIcon,
  TrashIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { PageTitle } from '@oysterjs/uiv2/page';
import { Badge } from '@oysterjs/uiv2/badge';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '@oysterjs/uiv2/table';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { CurrencyInput, Input, InputGroup, PercentageInput } from '@oysterjs/uiv2/input';
import { Button } from '@oysterjs/uiv2/button';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Legend
} from '@oysterjs/uiv2/fieldset';
import { PageNavigation } from '@oysterjs/uiv2/navigation';
import {
  AccountFieldsFragment,
  AddPolicyExplorerConversationMessageMutation,
  AgencyUserFieldsFragment,
  BusinessPolicyBillingType,
  BusinessPolicyCancellationReason,
  BusinessPolicyCancellationSource,
  BusinessPolicyCancellationType,
  BusinessPolicyDetailFieldsFragment,
  BusinessPolicyFieldsFragment,
  BusinessPolicyFileFieldsFragment,
  CancelBusinessPolicyInput,
  CancelBusinessPolicyMutation,
  CreateBusinessPoliciesInput,
  CreateBusinessPoliciesMutation,
  DeleteFileMutation,
  FileType,
  InsuranceCarrierFieldsFragment,
  InsuranceMarket,
  InsuranceType,
  PaymentIntervalType,
  PolicyExplorerConversationMessageFieldsFragment,
  PolicyExplorerConversationMessageRole,
  PolicyState,
  RenewBusinessPolicyInput,
  RenewBusinessPolicyMutation,
  UpdateBusinessPolicyInput,
  UpdateBusinessPolicyMutation,
  UploadFileInput,
  UploadPolicyFileMutation
} from '../../api/graphql';
import { AnimatePresence } from 'motion/react';
import { Await, TransitionDiv } from '@oysterjs/uiv2/async';
import { ActionResult } from '@oysterjs/corev2/graphql/actions';
import { ValidationError } from '@oysterjs/corev2/graphql';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
  MountableDialog
} from '@oysterjs/uiv2/dialog';
import { Combobox } from '@oysterjs/uiv2/combobox';
import { getDisplayInsuranceType } from '../../api/map';
import { Select } from '@oysterjs/uiv2/select';
import { DeepPartial } from '@oysterjs/corev2/graphql/types';
import { Textarea } from '@oysterjs/uiv2/textarea';
import { FileInput } from '@oysterjs/uiv2/file';
import { Spinner } from '@oysterjs/uiv2/spinner';

export const PoliciesPage = () => {
  const outlet = useOutlet();
  const location = useLocation();
  const loader = useLoaderData() as
    | { data: Promise<{ businessPolicies?: BusinessPolicyFieldsFragment[] }> }
    | undefined;

  return (
    <PageTitle title="Policies" description="View and manage your agency's policies">
      <div className="space-y-4">
        <div>
          <div className="flex justify-between">
            <form className="w-full max-w-md">
              <Fieldset>
                <FieldGroup>
                  <Field>
                    <Label className="sr-only" data-slot="none">
                      Filter table
                    </Label>
                    <InputGroup>
                      <MagnifyingGlassIcon className="h-4 w-4" />
                      <Input type="search" name="search" placeholder="Start typing to filter..." />
                    </InputGroup>
                  </Field>
                </FieldGroup>
              </Fieldset>
            </form>
            <Button href="/app/policies/new" color="sky">
              Add Policy
            </Button>
          </div>
        </div>
        <React.Suspense fallback={<PoliciesPageLoader />}>
          <Await resolve={loader?.data}>
            {({ businessPolicies }) => (
              <div className="px-6 rounded-lg bg-white shadow">
                {!businessPolicies?.length && (
                  <div className="text-center py-12">
                    <DocumentPlusIcon className="mx-auto size-12 text-neutral-400" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900">
                      No policies found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500">
                      You haven't added any policies yet. Create a new policy to get started.
                    </p>
                  </div>
                )}
                {!!businessPolicies?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4">Account</TableHeader>
                        <TableHeader className="py-4">Status</TableHeader>
                        <TableHeader className="py-4">Type</TableHeader>
                        <TableHeader className="py-4">Carrier</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {businessPolicies.map((policy) => (
                        <TableRow key={policy.id} href={`/app/policies/${policy.id}/overview`}>
                          <TableCell>
                            <div className="font-medium">{policy.account.account.name}</div>
                            <div className="text-neutral-500">{policy.account.account.email}</div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              // eslint-disable-next-line
                              // @ts-ignore
                              color={
                                {
                                  Pending: 'amber',
                                  Binding: 'sky',
                                  Active: 'green',
                                  Expired: 'neutral',
                                  Canceled: 'rose'
                                }[policy.state] || 'neutral'
                              }
                            >
                              {policy.state}
                            </Badge>
                          </TableCell>
                          <TableCell>{getDisplayInsuranceType(policy.type)}</TableCell>
                          <TableCell>
                            <div>{policy.carrier.name}</div>
                            <div className="text-neutral-500">{policy.policyNumber}</div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>
      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </PageTitle>
  );
};

export const PoliciesPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading policies...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

const activity = [
  {
    id: 1,
    type: 'created',
    person: { name: 'Chelsea Hagon' },
    date: '7d ago',
    dateTime: '2023-01-23T10:32'
  },
  {
    id: 2,
    type: 'edited',
    person: { name: 'Chelsea Hagon' },
    date: '6d ago',
    dateTime: '2023-01-23T11:03'
  },
  {
    id: 3,
    type: 'edited',
    person: { name: 'Chelsea Hagon' },
    date: '6d ago',
    dateTime: '2023-01-23T11:24'
  },
  {
    id: 5,
    type: 'edited',
    person: { name: 'Alex Curren' },
    date: '2d ago',
    dateTime: '2023-01-24T09:12'
  },
  {
    id: 6,
    type: 'canceled',
    person: { name: 'Alex Curren' },
    date: '1d ago',
    dateTime: '2023-01-24T09:20'
  }
];

export const PolicyCreatePage = () => {
  const navigate = useNavigate();
  const loader = useRouteLoaderData('policies/new') as
    | {
        data: Promise<{
          accounts?: AccountFieldsFragment[];
          agencyUsers?: AgencyUserFieldsFragment[];
          carriers?: InsuranceCarrierFieldsFragment[];
        }>;
      }
    | undefined;

  return (
    <MountableDialog onClose={() => navigate(-1)} size="2xl">
      <DialogTitle>Create a new policy</DialogTitle>
      <DialogDescription>Create a new policy and associate it with an account.</DialogDescription>
      <React.Suspense
        fallback={
          <TransitionDiv className="pt-2 flex justify-center">
            <Spinner size="size-8" color="neutral" />
          </TransitionDiv>
        }
      >
        <Await resolve={loader?.data}>
          <PolicyCreateForm />
        </Await>
      </React.Suspense>
    </MountableDialog>
  );
};

const PolicyCreateForm = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const submit = useSubmit();
  const promise = useAsyncValue() as
    | {
        accounts?: AccountFieldsFragment[];
        agencyUsers?: AgencyUserFieldsFragment[];
        carriers?: InsuranceCarrierFieldsFragment[];
      }
    | undefined;

  const action = useActionData() as ActionResult<CreateBusinessPoliciesMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<DeepPartial<CreateBusinessPoliciesInput>>({
    accountId: new URLSearchParams(location.search).get('accountId') || '',
    policies: [
      {
        // Generate a random ID for the policy for reference purposes in the UI
        id: '' + Math.floor(999_999_999 + Math.random() * 1_000_000_000),
        // Set some default values to avoid GraphQL validation errors
        quoteNumber: '',
        agencyFees: 0,
        brokerFees: 0,
        policyFees: 0,
        otherFees: 0,
        taxes: 0,
        // Set some default values for the form
        state: PolicyState.Binding,
        billingType: BusinessPolicyBillingType.Direct,
        paymentIntervalType: PaymentIntervalType.Annual,
        issuedAt: new Date(),
        inforceAt: new Date(),
        expiresAt: new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000),
        minimumEarnedPremium: 0
      }
    ]
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    updateForm(['policies', '0', 'total'], (prev) => ({
      ...prev,
      policies: [
        {
          ...(prev.policies?.[0] || {}),
          total:
            (prev.policies?.[0]?.premium || 0) +
            (prev.policies?.[0]?.taxes || 0) +
            (prev.policies?.[0]?.agencyFees || 0) +
            (prev.policies?.[0]?.brokerFees || 0) +
            (prev.policies?.[0]?.policyFees || 0) +
            (prev.policies?.[0]?.otherFees || 0)
        }
      ]
    }));
  }, [
    form.policies?.[0]?.premium,
    form.policies?.[0]?.taxes,
    form.policies?.[0]?.agencyFees,
    form.policies?.[0]?.brokerFees,
    form.policies?.[0]?.policyFees,
    form.policies?.[0]?.otherFees
  ]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();

    // Disabled type checker because react-router-dom defines a custom type for what is considered
    // JSON-serializable, which does not include Date objects. This doesn't actually affect anything,
    // the type checker just complains.
    // eslint-disable-next-line
    // @ts-ignore
    submit(form, { method: 'POST', encType: 'application/json' });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <Legend>Choose an account</Legend>
          <Description>
            Search for and select the account to associate this policy with.
          </Description>
          <FieldGroup>
            <Field>
              <Combobox
                value={form.accountId ?? ''}
                minQueryLength={0}
                maxOptions={1000}
                onChange={(accountId) =>
                  updateForm(['accountId'], (prev) => ({ ...prev, accountId }))
                }
                options={
                  promise?.accounts?.map((account) => ({
                    value: account.id,
                    label: account.account.name,
                    description: account.account.email
                  })) || []
                }
                error={getValidationError(validationErrors, ['accountId'])}
              />
            </Field>
          </FieldGroup>
        </Fieldset>
        {form.accountId && (
          <>
            <Fieldset>
              <Legend>Policy Information</Legend>
              <Description>Enter basic information about the policy</Description>
              <FieldGroup>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field>
                    <Label>Policy Type</Label>
                    <Combobox
                      minQueryLength={0}
                      maxOptions={1000}
                      value={form.policies?.[0]?.type || ''}
                      onChange={(type) =>
                        updateForm(['policies', '0', 'type'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              type: type as InsuranceType
                            }
                          ]
                        }))
                      }
                      options={Object.values(InsuranceType).map((type) => ({
                        value: type,
                        label: getDisplayInsuranceType(type)
                      }))}
                      error={getValidationError(validationErrors, ['policies', '0', 'type'])}
                    />
                  </Field>
                  <Field>
                    <Label>Market</Label>
                    <Combobox
                      minQueryLength={0}
                      maxOptions={1000}
                      value={form.policies?.[0]?.market || ''}
                      onChange={(market) =>
                        updateForm(['policies', '0', 'market'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              market: market as InsuranceMarket
                            }
                          ]
                        }))
                      }
                      options={[
                        {
                          value: InsuranceMarket.Admitted,
                          label: 'Admitted'
                        },
                        {
                          value: InsuranceMarket.ExcessAndSurplus,
                          label: 'Excess and Surplus'
                        }
                      ]}
                      error={getValidationError(validationErrors, ['policies', '0', 'market'])}
                    />
                  </Field>
                  <Field>
                    <Label>Carrier</Label>
                    <Combobox
                      minQueryLength={0}
                      maxOptions={1000}
                      value={form.policies?.[0]?.carrierId || ''}
                      onChange={(carrierId) =>
                        updateForm(['policies', '0', 'carrierId'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              carrierId
                            }
                          ]
                        }))
                      }
                      options={
                        promise?.carriers
                          ?.filter((carrier) => carrier.isCarrier)
                          .map((carrier) => ({
                            value: carrier.id,
                            label: carrier.name
                          })) || []
                      }
                      error={getValidationError(validationErrors, ['policies', '0', 'carrierId'])}
                    />
                  </Field>
                  <Field>
                    <Label>Wholesale Broker</Label>
                    <Combobox
                      minQueryLength={0}
                      maxOptions={1000}
                      value={form.policies?.[0]?.brokerId || ''}
                      onChange={(brokerId) =>
                        updateForm(['policies', '0', 'brokerId'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              brokerId: brokerId ? brokerId : undefined
                            }
                          ]
                        }))
                      }
                      options={
                        promise?.carriers
                          ?.filter((carrier) => carrier.isBroker)
                          .map((broker) => ({
                            value: broker.id,
                            label: broker.name
                          })) || []
                      }
                      error={getValidationError(validationErrors, ['policies', '0', 'brokerId'])}
                    />
                  </Field>
                  <Field>
                    <Label>Quote Number</Label>
                    <Input
                      value={form.policies?.[0]?.quoteNumber || ''}
                      onChange={(e) => {
                        const quoteNumber = e.currentTarget.value;
                        updateForm(['policies', '0', 'quoteNumber'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              quoteNumber
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'quoteNumber'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'quoteNumber']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'quoteNumber'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Policy Number</Label>
                    <Input
                      value={form.policies?.[0]?.policyNumber || ''}
                      onChange={(e) => {
                        const policyNumber = e.currentTarget.value;
                        updateForm(['policies', '0', 'policyNumber'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              policyNumber
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'policyNumber'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'policyNumber']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'policyNumber'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Agent</Label>
                    <Combobox
                      minQueryLength={0}
                      maxOptions={1000}
                      value={form.policies?.[0]?.agencyUserId || ''}
                      onChange={(agencyUserId) =>
                        updateForm(['policies', '0', 'agencyUserId'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              agencyUserId
                            }
                          ]
                        }))
                      }
                      options={
                        promise?.agencyUsers?.map((agent) => ({
                          value: agent.id,
                          label: agent.name,
                          description: agent.email
                        })) || []
                      }
                      error={getValidationError(validationErrors, [
                        'policies',
                        '0',
                        'agencyUserId'
                      ])}
                    />
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>
            <Fieldset>
              <Legend>Coverage Information</Legend>
              <Description>Specify the coverage details for this policy.</Description>
              <FieldGroup>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field>
                    <Label>Issuance Date</Label>
                    <Input
                      type="date"
                      value={
                        form.policies?.[0]?.issuedAt
                          ? new Date(form.policies?.[0]?.issuedAt).toISOString().split('T')[0]
                          : ''
                      }
                      onChange={(e) => {
                        const issuedAt = e.currentTarget.value;
                        updateForm(['policies', '0', 'issuedAt'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              issuedAt:
                                issuedAt && !isNaN(new Date(issuedAt).getTime())
                                  ? new Date(issuedAt)
                                  : undefined
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'issuedAt'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'issuedAt']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'issuedAt'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field>
                    <Label>Inforce Date</Label>
                    <Input
                      type="date"
                      value={
                        form.policies?.[0]?.inforceAt
                          ? new Date(form.policies?.[0]?.inforceAt).toISOString().split('T')[0]
                          : ''
                      }
                      onChange={(e) => {
                        const inforceAt = e.currentTarget.value;
                        updateForm(['policies', '0', 'inforceAt'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              inforceAt:
                                inforceAt && !isNaN(new Date(inforceAt).getTime())
                                  ? new Date(inforceAt)
                                  : undefined
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'inforceAt'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'inforceAt']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'inforceAt'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Expiration Date</Label>
                    <Input
                      type="date"
                      value={
                        form.policies?.[0]?.expiresAt
                          ? new Date(form.policies?.[0]?.expiresAt).toISOString().split('T')[0]
                          : ''
                      }
                      onChange={(e) => {
                        const expiresAt = e.currentTarget.value;
                        updateForm(['policies', '0', 'expiresAt'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              expiresAt:
                                expiresAt && !isNaN(new Date(expiresAt).getTime())
                                  ? new Date(expiresAt)
                                  : undefined
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'expiresAt'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'expiresAt']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'expiresAt'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>
            <Fieldset>
              <Legend>Billing Information</Legend>
              <Description>Specify the payment details for this policy.</Description>
              <FieldGroup>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field>
                    <Label>Billing Type</Label>
                    <Select
                      value={form.policies?.[0]?.billingType || ''}
                      onChange={(e) => {
                        const billingType = e.currentTarget.value;
                        updateForm(['policies', '0', 'billingType'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              billingType: billingType as BusinessPolicyBillingType
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'billingType'])
                      }
                    >
                      <option value="" disabled>
                        Select a billing type
                      </option>
                      <option value={BusinessPolicyBillingType.Direct}>Direct</option>
                      <option value={BusinessPolicyBillingType.Agency}>Agency</option>
                    </Select>
                    {getValidationError(validationErrors, ['policies', '0', 'billingType']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'billingType'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Payment Cadence</Label>
                    <Select
                      value={form.policies?.[0]?.paymentIntervalType || ''}
                      onChange={(e) => {
                        const paymentIntervalType = e.currentTarget.value;
                        updateForm(['policies', '0', 'paymentIntervalType'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              paymentIntervalType: paymentIntervalType as PaymentIntervalType
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, [
                          'policies',
                          '0',
                          'paymentIntervalType'
                        ])
                      }
                    >
                      <option value="" disabled>
                        Select a payment cadence
                      </option>
                      <option value={PaymentIntervalType.Annual}>Annual</option>
                      <option value={PaymentIntervalType.Monthly}>Monthly</option>
                      <option value={PaymentIntervalType.Quarterly}>Quarterly</option>
                    </Select>
                    {getValidationError(validationErrors, [
                      'policies',
                      '0',
                      'paymentIntervalType'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'policies',
                          '0',
                          'paymentIntervalType'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>
            <Fieldset>
              <Legend>Financial Information</Legend>
              <Description>
                Enter the premium, commission, and any other taxes and fees that apply to this
                policy.
              </Description>
              <FieldGroup>
                <div className="grid xs:grid-cols-2 sm:grid-cols-3 gap-4">
                  <Field>
                    <Label>Premium</Label>
                    <CurrencyInput
                      value={form.policies?.[0]?.premium ?? ''}
                      onChange={(premium) => {
                        updateForm(['policies', '0', 'premium'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              premium: isNaN(premium) ? undefined : premium
                            }
                          ]
                        }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['policies', '0', 'premium'])}
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'premium']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'premium'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Commission Rate</Label>
                    <PercentageInput
                      value={
                        form.policies?.[0]?.premium
                          ? (form.policies?.[0]?.estimatedCommission || 0) /
                            form.policies?.[0]?.premium
                          : ''
                      }
                      onChange={(commissionRate) => {
                        updateForm(['policies', '0', 'estimatedCommission'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              estimatedCommission: isNaN(commissionRate)
                                ? undefined
                                : commissionRate * (prev.policies?.[0]?.premium || 0)
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, [
                          'policies',
                          '0',
                          'estimatedCommission'
                        ])
                      }
                    />
                    {getValidationError(validationErrors, [
                      'policies',
                      '0',
                      'estimatedCommission'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'policies',
                          '0',
                          'estimatedCommission'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Estimated Commission</Label>
                    <CurrencyInput disabled value={form.policies?.[0]?.estimatedCommission ?? ''} />
                  </Field>
                  <Field>
                    <Label>Agency Fees</Label>
                    <CurrencyInput
                      value={form.policies?.[0]?.agencyFees ?? ''}
                      onChange={(agencyFees) => {
                        updateForm(['policies', '0', 'agencyFees'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              agencyFees: isNaN(agencyFees) ? undefined : agencyFees
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'agencyFees'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'agencyFees']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'agencyFees'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Policy Fees</Label>
                    <CurrencyInput
                      value={form.policies?.[0]?.policyFees ?? ''}
                      onChange={(policyFees) => {
                        updateForm(['policies', '0', 'policyFees'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              policyFees: isNaN(policyFees) ? undefined : policyFees
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'policyFees'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'policyFees']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'policyFees'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Broker Fees</Label>
                    <CurrencyInput
                      value={form.policies?.[0]?.brokerFees ?? ''}
                      onChange={(brokerFees) => {
                        updateForm(['policies', '0', 'brokerFees'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              brokerFees: isNaN(brokerFees) ? undefined : brokerFees
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'brokerFees'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'brokerFees']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'brokerFees'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Other Fees</Label>
                    <CurrencyInput
                      value={form.policies?.[0]?.otherFees ?? ''}
                      onChange={(otherFees) => {
                        updateForm(['policies', '0', 'otherFees'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              otherFees: isNaN(otherFees) ? undefined : otherFees
                            }
                          ]
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['policies', '0', 'otherFees'])
                      }
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'otherFees']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'otherFees'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Taxes</Label>
                    <CurrencyInput
                      value={form.policies?.[0]?.taxes ?? ''}
                      onChange={(taxes) => {
                        updateForm(['policies', '0', 'taxes'], (prev) => ({
                          ...prev,
                          policies: [
                            {
                              ...(prev.policies?.[0] || {}),
                              taxes: isNaN(taxes) ? undefined : taxes
                            }
                          ]
                        }));
                      }}
                      invalid={!!getValidationError(validationErrors, ['policies', '0', 'taxes'])}
                    />
                    {getValidationError(validationErrors, ['policies', '0', 'taxes']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['policies', '0', 'taxes'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Total</Label>
                    <CurrencyInput disabled value={form.policies?.[0]?.total ?? ''} />
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>
          </>
        )}
      </DialogBody>
      <DialogActions>
        <Button
          type="button"
          color="white"
          disabled={navigation.state === 'submitting'}
          onClick={() => navigate(-1)}
        >
          Close
        </Button>
        <Button
          type="submit"
          color="sky"
          disabled={!form}
          loading={navigation.state === 'submitting'}
        >
          Create Policy
        </Button>
      </DialogActions>
    </Form>
  );
};

export const PolicyEditPage = () => {
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          agencyUsers?: AgencyUserFieldsFragment[];
          businessPolicy?: BusinessPolicyFieldsFragment;
          carriers?: InsuranceCarrierFieldsFragment[];
        }>;
      }
    | undefined;

  return (
    <MountableDialog onClose={() => navigate(-1)} size="2xl">
      <DialogTitle>Edit policy</DialogTitle>
      <DialogDescription>Edit the details of this policy.</DialogDescription>
      <React.Suspense>
        <Await resolve={loader?.data}>
          <PolicyEditForm />
        </Await>
      </React.Suspense>
    </MountableDialog>
  );
};

const PolicyEditForm = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<UpdateBusinessPolicyMutation>;
  const promise = useAsyncValue() as {
    agencyUsers?: AgencyUserFieldsFragment[];
    businessPolicy?: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
    carriers?: InsuranceCarrierFieldsFragment[];
  };

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<UpdateBusinessPolicyInput>({
    updateReason: ''
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    updateForm(['total'], (prev) => ({
      ...prev,
      total:
        (prev.premium || 0) +
        (prev.taxes || 0) +
        (prev.agencyFees || 0) +
        (prev.brokerFees || 0) +
        (prev.policyFees || 0) +
        (prev.otherFees || 0)
    }));
  }, [form.premium, form.taxes, form.agencyFees, form.brokerFees, form.policyFees, form.otherFees]);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (promise?.businessPolicy) {
      setForm({
        // Set the form from the current business policy
        type: promise?.businessPolicy.type,
        state: promise?.businessPolicy.state,
        market: promise?.businessPolicy.market,
        carrierId: promise?.businessPolicy.carrier?.id,
        brokerId: promise?.businessPolicy.broker?.id,
        quoteNumber: promise?.businessPolicy.quoteNumber,
        policyNumber: promise?.businessPolicy.policyNumber,
        agencyUserId: promise?.businessPolicy.agencyUser?.id,
        issuedAt: promise?.businessPolicy.issuedAt,
        inforceAt: promise?.businessPolicy.inforceAt,
        expiresAt: promise?.businessPolicy.expiresAt,
        total: promise?.businessPolicy.pricing.total,
        premium: promise?.businessPolicy.pricing.premium,
        agencyFees: promise?.businessPolicy.pricing.agencyFees,
        brokerFees: promise?.businessPolicy.pricing.brokerFees,
        policyFees: promise?.businessPolicy.pricing.policyFees,
        otherFees: promise?.businessPolicy.pricing.otherFees,
        taxes: promise?.businessPolicy.pricing.taxes,
        estimatedCommission: promise?.businessPolicy.pricing.estimatedCommission,
        // Set default fields
        updateReason: ''
      });
    }
  }, [promise?.businessPolicy]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();

    // Disabled type checker because react-router-dom defines a custom type for what is considered
    // JSON-serializable, which does not include Date objects. This doesn't actually affect anything,
    // the type checker just complains.
    // eslint-disable-next-line
    // @ts-ignore
    submit(form, { method: 'PUT', encType: 'application/json' });
  };

  return (
    <Form onSubmit={handleSubmit}>
      <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
        <Fieldset>
          <Legend>Policy Information</Legend>
          <Description>Enter basic information about the policy</Description>
          <FieldGroup>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field>
                <Label>Policy Type</Label>
                <Combobox
                  minQueryLength={0}
                  maxOptions={1000}
                  value={form.type || ''}
                  onChange={(type) =>
                    updateForm(['type'], (prev) => ({
                      ...prev,
                      type: type ? (type as InsuranceType) : prev.type
                    }))
                  }
                  options={Object.values(InsuranceType).map((type) => ({
                    value: type,
                    label: getDisplayInsuranceType(type)
                  }))}
                  error={getValidationError(validationErrors, ['type'])}
                />
              </Field>
              <Field>
                <Label>Market</Label>
                <Combobox
                  minQueryLength={0}
                  maxOptions={1000}
                  value={form.market || ''}
                  onChange={(market) =>
                    updateForm(['market'], (prev) => ({
                      ...prev,
                      market: market ? (market as InsuranceMarket) : prev.market
                    }))
                  }
                  options={[
                    {
                      value: InsuranceMarket.Admitted,
                      label: 'Admitted'
                    },
                    {
                      value: InsuranceMarket.ExcessAndSurplus,
                      label: 'Excess and Surplus'
                    }
                  ]}
                  error={getValidationError(validationErrors, ['market'])}
                />
              </Field>
              <Field>
                <Label>Carrier</Label>
                <Combobox
                  minQueryLength={0}
                  maxOptions={1000}
                  value={form.carrierId || ''}
                  onChange={(carrierId) =>
                    updateForm(['carrierId'], (prev) => ({
                      ...prev,
                      carrierId: carrierId ? carrierId : prev.carrierId
                    }))
                  }
                  options={
                    promise?.carriers
                      ?.filter((carrier) => carrier.isCarrier)
                      .map((carrier) => ({
                        value: carrier.id,
                        label: carrier.name
                      })) || []
                  }
                  error={getValidationError(validationErrors, ['carrierId'])}
                />
              </Field>
              <Field>
                <Label>Wholesale Broker</Label>
                <Combobox
                  minQueryLength={0}
                  maxOptions={1000}
                  value={form.brokerId || ''}
                  onChange={(brokerId) =>
                    updateForm(['brokerId'], (prev) => ({
                      ...prev,
                      brokerId: brokerId ? brokerId : null
                    }))
                  }
                  options={
                    promise?.carriers
                      ?.filter((carrier) => carrier.isBroker)
                      .map((broker) => ({
                        value: broker.id,
                        label: broker.name
                      })) || []
                  }
                  error={getValidationError(validationErrors, ['brokerId'])}
                />
              </Field>
              <Field>
                <Label>Quote Number</Label>
                <Input
                  value={form.quoteNumber || ''}
                  onChange={(e) => {
                    const quoteNumber = e.currentTarget.value;
                    updateForm(['quoteNumber'], (prev) => ({
                      ...prev,
                      quoteNumber: quoteNumber
                    }));
                  }}
                  onBlur={(e) => {
                    const quoteNumber = e.currentTarget.value;
                    updateForm(['quoteNumber'], (prev) => ({
                      ...prev,
                      quoteNumber: quoteNumber || promise?.businessPolicy?.quoteNumber
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['quoteNumber'])}
                />
                {getValidationError(validationErrors, ['quoteNumber']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['quoteNumber'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Policy Number</Label>
                <Input
                  value={form.policyNumber || ''}
                  onChange={(e) => {
                    const policyNumber = e.currentTarget.value;
                    updateForm(['policyNumber'], (prev) => ({
                      ...prev,
                      policyNumber
                    }));
                  }}
                  onBlur={(e) => {
                    const policyNumber = e.currentTarget.value;
                    updateForm(['policyNumber'], (prev) => ({
                      ...prev,
                      policyNumber: policyNumber || promise?.businessPolicy?.policyNumber
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['policyNumber'])}
                />
                {getValidationError(validationErrors, ['policyNumber']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['policyNumber'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Agent</Label>
                <Combobox
                  minQueryLength={0}
                  maxOptions={1000}
                  value={form.agencyUserId || ''}
                  onChange={(agencyUserId) =>
                    updateForm(['agencyUserId'], (prev) => ({
                      ...prev,
                      agencyUserId
                    }))
                  }
                  options={
                    promise?.agencyUsers?.map((agent) => ({
                      value: agent.id,
                      label: agent.name,
                      description: agent.email
                    })) || []
                  }
                  error={getValidationError(validationErrors, ['agencyUserId'])}
                />
              </Field>
            </div>
          </FieldGroup>
        </Fieldset>
        <Fieldset>
          <Legend>Coverage Information</Legend>
          <Description>Specify the coverage details for this policy.</Description>
          <FieldGroup>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field>
                <Label>Issuance Date</Label>
                <Input
                  type="date"
                  value={form.issuedAt ? new Date(form.issuedAt).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const issuedAt = e.currentTarget.value;
                    updateForm(['issuedAt'], (prev) => ({
                      ...prev,
                      issuedAt:
                        issuedAt && !isNaN(new Date(issuedAt).getTime())
                          ? new Date(issuedAt)
                          : undefined
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['issuedAt'])}
                />
                {getValidationError(validationErrors, ['issuedAt']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['issuedAt'])}</ErrorMessage>
                )}
              </Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field>
                <Label>Inforce Date</Label>
                <Input
                  type="date"
                  value={form.inforceAt ? new Date(form.inforceAt).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const inforceAt = e.currentTarget.value;
                    updateForm(['inforceAt'], (prev) => ({
                      ...prev,
                      inforceAt:
                        inforceAt && !isNaN(new Date(inforceAt).getTime())
                          ? new Date(inforceAt)
                          : undefined
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['inforceAt'])}
                />
                {getValidationError(validationErrors, ['inforceAt']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['inforceAt'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Expiration Date</Label>
                <Input
                  type="date"
                  value={form.expiresAt ? new Date(form.expiresAt).toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const expiresAt = e.currentTarget.value;
                    updateForm(['expiresAt'], (prev) => ({
                      ...prev,
                      expiresAt:
                        expiresAt && !isNaN(new Date(expiresAt).getTime())
                          ? new Date(expiresAt)
                          : undefined
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['expiresAt'])}
                />
                {getValidationError(validationErrors, ['expiresAt']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['expiresAt'])}</ErrorMessage>
                )}
              </Field>
            </div>
          </FieldGroup>
        </Fieldset>
        <Fieldset>
          <Legend>Financial Information</Legend>
          <Description>
            Enter the premium, commission, and any other taxes and fees that apply to this policy.
          </Description>
          <FieldGroup>
            <div className="grid xs:grid-cols-2 sm:grid-cols-3 gap-4">
              <Field>
                <Label>Premium</Label>
                <CurrencyInput
                  value={form.premium ?? undefined}
                  onChange={(premium) => {
                    updateForm(['premium'], (prev) => ({
                      ...prev,
                      premium: isNaN(premium) ? promise?.businessPolicy?.pricing.premium : premium
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['premium'])}
                />
                {getValidationError(validationErrors, ['premium']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['premium'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Commission Rate</Label>
                <PercentageInput
                  value={form.premium ? (form.estimatedCommission || 0) / form.premium : undefined}
                  onChange={(commissionRate) => {
                    updateForm(['estimatedCommission'], (prev) => ({
                      ...prev,
                      estimatedCommission: isNaN(commissionRate)
                        ? promise?.businessPolicy?.pricing.estimatedCommission
                        : commissionRate * (prev.premium || 0)
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['estimatedCommission'])}
                />
                {getValidationError(validationErrors, ['estimatedCommission']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['estimatedCommission'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Estimated Commission</Label>
                <CurrencyInput disabled value={form.estimatedCommission ?? undefined} />
              </Field>
              <Field>
                <Label>Agency Fees</Label>
                <CurrencyInput
                  value={form.agencyFees ?? undefined}
                  onChange={(agencyFees) => {
                    updateForm(['agencyFees'], (prev) => ({
                      ...prev,
                      agencyFees: isNaN(agencyFees)
                        ? promise?.businessPolicy?.pricing.agencyFees
                        : agencyFees
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['agencyFees'])}
                />
                {getValidationError(validationErrors, ['agencyFees']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['agencyFees'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Policy Fees</Label>
                <CurrencyInput
                  value={form.policyFees ?? undefined}
                  onChange={(policyFees) => {
                    updateForm(['policyFees'], (prev) => ({
                      ...prev,
                      policyFees: isNaN(policyFees)
                        ? promise?.businessPolicy?.pricing.policyFees
                        : policyFees
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['policyFees'])}
                />
                {getValidationError(validationErrors, ['policyFees']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['policyFees'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Broker Fees</Label>
                <CurrencyInput
                  value={form.brokerFees ?? undefined}
                  onChange={(brokerFees) => {
                    updateForm(['brokerFees'], (prev) => ({
                      ...prev,
                      brokerFees: isNaN(brokerFees)
                        ? promise?.businessPolicy?.pricing.brokerFees
                        : brokerFees
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['brokerFees'])}
                />
                {getValidationError(validationErrors, ['brokerFees']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['brokerFees'])}
                  </ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Other Fees</Label>
                <CurrencyInput
                  value={form.otherFees ?? undefined}
                  onChange={(otherFees) => {
                    updateForm(['otherFees'], (prev) => ({
                      ...prev,
                      otherFees: isNaN(otherFees)
                        ? promise?.businessPolicy?.pricing.otherFees
                        : otherFees
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['otherFees'])}
                />
                {getValidationError(validationErrors, ['otherFees']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['otherFees'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Taxes</Label>
                <CurrencyInput
                  value={form.taxes ?? undefined}
                  onChange={(taxes) => {
                    updateForm(['taxes'], (prev) => ({
                      ...prev,
                      taxes: isNaN(taxes) ? promise?.businessPolicy?.pricing.taxes : taxes
                    }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['taxes'])}
                />
                {getValidationError(validationErrors, ['taxes']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['taxes'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>Total</Label>
                <CurrencyInput disabled value={form.total ?? undefined} />
              </Field>
            </div>
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button
          type="button"
          color="white"
          disabled={navigation.state === 'submitting'}
          onClick={() => navigate(-1)}
        >
          Close
        </Button>
        <Button
          type="submit"
          color="sky"
          disabled={!form}
          loading={navigation.state === 'submitting'}
        >
          Update Policy
        </Button>
      </DialogActions>
    </Form>
  );
};

export const PolicyPage = () => {
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);
  const [showRenewDialog, setShowRenewDialog] = React.useState(false);

  const loader = useLoaderData() as
    | {
        data: Promise<{
          businessPolicy?: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense fallback={<Spinner size="size-8" color="neutral" />}>
      <Await resolve={loader?.data}>
        {({ businessPolicy }) => (
          <>
            <div className="lg:flex lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="m-0 text-2xl font-semibold text-neutral-900 sm:truncate sm:text-3xl sm:tracking-tight">
                  {getDisplayInsuranceType(businessPolicy?.type)}
                </h2>
                <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
                  <div className="mt-2 flex items-center text-sm text-neutral-500">
                    <UserIcon
                      aria-hidden="true"
                      className="mr-1.5 size-5 shrink-0 text-neutral-400"
                    />
                    {businessPolicy?.account.contacts[0].name}
                  </div>
                  <div className="mt-2 flex items-center text-sm text-neutral-500">
                    <BriefcaseIcon
                      aria-hidden="true"
                      className="mr-1.5 size-5 shrink-0 text-neutral-400"
                    />
                    {businessPolicy?.account.account.name}
                  </div>
                  <div className="mt-2 flex items-center text-sm text-neutral-500">
                    <CurrencyDollarIcon
                      aria-hidden="true"
                      className="mr-1.5 size-5 shrink-0 text-neutral-400"
                    />
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    }).format(businessPolicy?.pricing.premium || 0)}
                  </div>
                  <div className="mt-2 flex items-center text-sm text-neutral-500">
                    <CalendarIcon
                      aria-hidden="true"
                      className="mr-1.5 size-5 shrink-0 text-neutral-400"
                    />
                    {businessPolicy?.expiresAt && (
                      <>
                        Expires on{' '}
                        {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                          new Date(businessPolicy?.expiresAt)
                        )}
                      </>
                    )}
                    {!businessPolicy?.expiresAt && 'N/A'}
                  </div>
                </div>
              </div>
              <div className="mt-5 flex lg:ml-4 lg:mt-0">
                <span className="hidden sm:block">
                  <Button
                    type="button"
                    color="sky"
                    href={`/app/policies/${businessPolicy?.id}/edit`}
                  >
                    <PencilIcon
                      aria-hidden="true"
                      className="-ml-0.5 mr-1.5 size-5 text-white-400"
                    />
                    Edit
                  </Button>
                </span>
                <Headless.Menu as="div" className="relative ml-3">
                  <Headless.MenuButton className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 hover:ring-neutral-400">
                    More
                    <ChevronDownIcon
                      aria-hidden="true"
                      className="-mr-1 ml-1.5 size-5 text-neutral-400"
                    />
                  </Headless.MenuButton>

                  <Headless.MenuItems
                    transition
                    className="absolute right-0 z-10 -mr-1 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <Headless.MenuItem disabled={!!businessPolicy?.renewal?.nextPolicies?.length}>
                      <span
                        role="button"
                        onClick={() =>
                          !businessPolicy?.renewal?.nextPolicies?.length &&
                          setShowDeleteDialog(true)
                        }
                        className="block px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none data-[disabled]:opacity-50"
                      >
                        Delete
                      </span>
                    </Headless.MenuItem>
                    <Headless.MenuItem
                      disabled={!!businessPolicy?.cancellation && !businessPolicy.reinstatement}
                    >
                      <span
                        role="button"
                        onClick={() =>
                          (!businessPolicy?.cancellation || !!businessPolicy.reinstatement) &&
                          setShowCancelDialog(true)
                        }
                        className="block px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none data-[disabled]:opacity-50"
                      >
                        Cancel
                      </span>
                    </Headless.MenuItem>
                    <Headless.MenuItem
                      disabled={!businessPolicy?.cancellation || !!businessPolicy.reinstatement}
                    >
                      <span
                        role="button"
                        onClick={() =>
                          !!businessPolicy?.cancellation && !businessPolicy.reinstatement
                        }
                        className="block px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none data-[disabled]:opacity-50"
                      >
                        Reinstate
                      </span>
                    </Headless.MenuItem>
                    <Headless.MenuItem disabled={!!businessPolicy?.renewal?.nextPolicies?.length}>
                      <span
                        role="button"
                        onClick={() =>
                          !businessPolicy?.renewal?.nextPolicies?.length && setShowRenewDialog(true)
                        }
                        className="block px-4 py-2 text-sm text-neutral-700 data-[focus]:bg-neutral-100 data-[focus]:outline-none data-[disabled]:opacity-50"
                      >
                        Renew
                      </span>
                    </Headless.MenuItem>
                  </Headless.MenuItems>
                </Headless.Menu>
              </div>
            </div>
            <PageNavigation
              routes={[
                {
                  path: 'overview',
                  title: 'Overview'
                },
                {
                  path: 'documents',
                  title: 'Documents'
                },
                // {
                //   path: 'history',
                //   title: 'History'
                // },
                {
                  path: 'explore',
                  title: 'Explore with AI'
                }
              ]}
            />
            {businessPolicy && (
              <PolicyDeleteDialog
                open={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                businessPolicy={businessPolicy}
              />
            )}
            {businessPolicy && (
              <PolicyCancelDialog
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                businessPolicy={businessPolicy}
              />
            )}
            {businessPolicy && (
              <PolicyRenewDialog
                open={showRenewDialog}
                onClose={() => setShowRenewDialog(false)}
                businessPolicy={businessPolicy}
              />
            )}
          </>
        )}
      </Await>
    </React.Suspense>
  );
};

const PolicyDeleteDialog = (props: {
  open: boolean;
  onClose: () => void;
  businessPolicy: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
}) => {
  const submit = useSubmit();
  const navigation = useNavigation();

  const loading = navigation.state === 'submitting';
  const onDelete = () => submit({}, { method: 'DELETE', encType: 'application/json' });

  return (
    <Dialog open={props.open} onClose={props.onClose} size="md">
      <DialogTitle>Delete Policy</DialogTitle>
      <DialogDescription>
        Are you sure you want to delete this{' '}
        <strong>{getDisplayInsuranceType(props.businessPolicy.type)}</strong> policy for{' '}
        <strong>{props.businessPolicy.account.account.name}</strong>? This action is permanent and
        the policy will no longer be visible to the client (if applicable).
      </DialogDescription>
      <DialogActions>
        <Button type="button" color="white" onClick={props.onClose} disabled={loading}>
          Cancel
        </Button>
        <Button type="button" color="red" onClick={onDelete} loading={loading}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PolicyRenewDialog = (props: {
  open: boolean;
  onClose: () => void;
  businessPolicy: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<RenewBusinessPolicyMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<RenewBusinessPolicyInput>({
    state: PolicyState.Binding,
    quoteNumber: '',
    policyNumber: '',
    issuedAt: new Date(),
    inforceAt: new Date(),
    expiresAt: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
    billingType: props.businessPolicy.pricing.billingType,
    total: props.businessPolicy.pricing.total,
    premium: props.businessPolicy.pricing.premium,
    agencyFees: props.businessPolicy.pricing.agencyFees,
    brokerFees: props.businessPolicy.pricing.brokerFees,
    policyFees: props.businessPolicy.pricing.policyFees,
    otherFees: props.businessPolicy.pricing.otherFees,
    taxes: props.businessPolicy.pricing.taxes,
    estimatedCommission: props.businessPolicy.pricing.estimatedCommission
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (
      navigation.state === 'loading' &&
      !!action?.data?.renewBusinessPolicy?.id &&
      !action?.validationErrors?.length
    ) {
      props.onClose();
    }
  }, [navigation.state, action?.data?.renewBusinessPolicy?.id, action?.validationErrors?.length]);

  React.useEffect(() => {
    if (props?.businessPolicy) {
      setForm({
        state: PolicyState.Binding,
        quoteNumber: '',
        policyNumber: '',
        issuedAt: new Date(),
        inforceAt: new Date(),
        expiresAt: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        billingType: props.businessPolicy.pricing.billingType,
        total: props.businessPolicy.pricing.total,
        premium: props.businessPolicy.pricing.premium,
        agencyFees: props.businessPolicy.pricing.agencyFees,
        brokerFees: props.businessPolicy.pricing.brokerFees,
        policyFees: props.businessPolicy.pricing.policyFees,
        otherFees: props.businessPolicy.pricing.otherFees,
        taxes: props.businessPolicy.pricing.taxes,
        estimatedCommission: props.businessPolicy.pricing.estimatedCommission
      });
    }
  }, [props?.businessPolicy]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();

    // Disabled type checker because react-router-dom defines a custom type for what is considered
    // JSON-serializable, which does not include Date objects. This doesn't actually affect anything,
    // the type checker just complains.
    // eslint-disable-next-line
    // @ts-ignore
    submit(form, { action: 'renew', method: 'POST', encType: 'application/json' });
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} size="2xl">
      <DialogTitle>Renew policy</DialogTitle>
      <DialogDescription>
        Creates a new policy with the same details as the current one, allowing for some
        modifications. The new policy will be linked to the prior period policy.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
          <Fieldset>
            <Legend>Policy Information</Legend>
            <Description>Enter basic information about the policy</Description>
            <FieldGroup>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field>
                  <Label>Quote Number</Label>
                  <Input
                    value={form.quoteNumber || ''}
                    onChange={(e) => {
                      const quoteNumber = e.currentTarget.value;
                      updateForm(['quoteNumber'], (prev) => ({
                        ...prev,
                        quoteNumber: quoteNumber
                      }));
                    }}
                    onBlur={(e) => {
                      const quoteNumber = e.currentTarget.value;
                      updateForm(['quoteNumber'], (prev) => ({
                        ...prev,
                        quoteNumber: quoteNumber
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['quoteNumber'])}
                  />
                  {getValidationError(validationErrors, ['quoteNumber']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['quoteNumber'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Policy Number</Label>
                  <Input
                    value={form.policyNumber || ''}
                    onChange={(e) => {
                      const policyNumber = e.currentTarget.value;
                      updateForm(['policyNumber'], (prev) => ({
                        ...prev,
                        policyNumber
                      }));
                    }}
                    onBlur={(e) => {
                      const policyNumber = e.currentTarget.value;
                      updateForm(['policyNumber'], (prev) => ({
                        ...prev,
                        policyNumber: policyNumber
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['policyNumber'])}
                  />
                  {getValidationError(validationErrors, ['policyNumber']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['policyNumber'])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
          <Fieldset>
            <Legend>Coverage Information</Legend>
            <Description>Specify the coverage details for this policy.</Description>
            <FieldGroup>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field>
                  <Label>Issuance Date</Label>
                  <Input
                    type="date"
                    value={form.issuedAt ? new Date(form.issuedAt).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const issuedAt = e.currentTarget.value;
                      updateForm(['issuedAt'], (prev) => ({
                        ...prev,
                        issuedAt:
                          issuedAt && !isNaN(new Date(issuedAt).getTime())
                            ? new Date(issuedAt)
                            : undefined
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['issuedAt'])}
                  />
                  {getValidationError(validationErrors, ['issuedAt']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['issuedAt'])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field>
                  <Label>Inforce Date</Label>
                  <Input
                    type="date"
                    value={
                      form.inforceAt ? new Date(form.inforceAt).toISOString().split('T')[0] : ''
                    }
                    onChange={(e) => {
                      const inforceAt = e.currentTarget.value;
                      updateForm(['inforceAt'], (prev) => ({
                        ...prev,
                        inforceAt:
                          inforceAt && !isNaN(new Date(inforceAt).getTime())
                            ? new Date(inforceAt)
                            : undefined
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['inforceAt'])}
                  />
                  {getValidationError(validationErrors, ['inforceAt']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['inforceAt'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Expiration Date</Label>
                  <Input
                    type="date"
                    value={
                      form.expiresAt ? new Date(form.expiresAt).toISOString().split('T')[0] : ''
                    }
                    onChange={(e) => {
                      const expiresAt = e.currentTarget.value;
                      updateForm(['expiresAt'], (prev) => ({
                        ...prev,
                        expiresAt:
                          expiresAt && !isNaN(new Date(expiresAt).getTime())
                            ? new Date(expiresAt)
                            : undefined
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['expiresAt'])}
                  />
                  {getValidationError(validationErrors, ['expiresAt']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['expiresAt'])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
          <Fieldset>
            <Legend>Financial Information</Legend>
            <Description>
              Enter the premium, commission, and any other taxes and fees that apply to this policy.
            </Description>
            <FieldGroup>
              <div className="grid xs:grid-cols-2 sm:grid-cols-3 gap-4">
                <Field>
                  <Label>Premium</Label>
                  <CurrencyInput
                    value={form.premium ?? undefined}
                    onChange={(premium) => {
                      updateForm(['premium'], (prev) => ({
                        ...prev,
                        premium: isNaN(premium) ? prev.premium : premium
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['premium'])}
                  />
                  {getValidationError(validationErrors, ['premium']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['premium'])}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Commission Rate</Label>
                  <PercentageInput
                    value={
                      form.premium ? (form.estimatedCommission || 0) / form.premium : undefined
                    }
                    onChange={(commissionRate) => {
                      updateForm(['estimatedCommission'], (prev) => ({
                        ...prev,
                        estimatedCommission: isNaN(commissionRate)
                          ? prev.estimatedCommission
                          : commissionRate * (prev.premium || 0)
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['estimatedCommission'])}
                  />
                  {getValidationError(validationErrors, ['estimatedCommission']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['estimatedCommission'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Estimated Commission</Label>
                  <CurrencyInput disabled value={form.estimatedCommission ?? undefined} />
                </Field>
                <Field>
                  <Label>Agency Fees</Label>
                  <CurrencyInput
                    value={form.agencyFees ?? undefined}
                    onChange={(agencyFees) => {
                      updateForm(['agencyFees'], (prev) => ({
                        ...prev,
                        agencyFees: isNaN(agencyFees) ? prev.agencyFees : agencyFees
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['agencyFees'])}
                  />
                  {getValidationError(validationErrors, ['agencyFees']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['agencyFees'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Policy Fees</Label>
                  <CurrencyInput
                    value={form.policyFees ?? undefined}
                    onChange={(policyFees) => {
                      updateForm(['policyFees'], (prev) => ({
                        ...prev,
                        policyFees: isNaN(policyFees) ? prev.policyFees : policyFees
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['policyFees'])}
                  />
                  {getValidationError(validationErrors, ['policyFees']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['policyFees'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Broker Fees</Label>
                  <CurrencyInput
                    value={form.brokerFees ?? undefined}
                    onChange={(brokerFees) => {
                      updateForm(['brokerFees'], (prev) => ({
                        ...prev,
                        brokerFees: isNaN(brokerFees) ? prev.brokerFees : brokerFees
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['brokerFees'])}
                  />
                  {getValidationError(validationErrors, ['brokerFees']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['brokerFees'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Other Fees</Label>
                  <CurrencyInput
                    value={form.otherFees ?? undefined}
                    onChange={(otherFees) => {
                      updateForm(['otherFees'], (prev) => ({
                        ...prev,
                        otherFees: isNaN(otherFees) ? prev.otherFees : otherFees
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['otherFees'])}
                  />
                  {getValidationError(validationErrors, ['otherFees']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['otherFees'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Taxes</Label>
                  <CurrencyInput
                    value={form.taxes ?? undefined}
                    onChange={(taxes) => {
                      updateForm(['taxes'], (prev) => ({
                        ...prev,
                        taxes: isNaN(taxes) ? prev.taxes : taxes
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['taxes'])}
                  />
                  {getValidationError(validationErrors, ['taxes']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['taxes'])}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Total</Label>
                  <CurrencyInput disabled value={form.total ?? undefined} />
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          <Button
            type="submit"
            color="sky"
            disabled={!form}
            loading={navigation.state === 'submitting'}
          >
            Renew Policy
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

const PolicyCancelDialog = (props: {
  open: boolean;
  onClose: () => void;
  businessPolicy: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
}) => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CancelBusinessPolicyMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CancelBusinessPolicyInput>({
    canceledBy: BusinessPolicyCancellationSource.Carrier,
    effectiveAt: new Date(),
    type: BusinessPolicyCancellationType.Cancellation,
    reason: BusinessPolicyCancellationReason.InsuredNonpayment,
    reasonDescription: '',
    returnPremium: 0,
    returnCommission: 0
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (navigation.state === 'loading' && navigation.formAction?.endsWith('/cancel')) {
      props.onClose();
    }
  }, [
    navigation.state,
    navigation.location?.pathname,
    action?.data?.cancelBusinessPolicy?.id,
    action?.validationErrors?.length
  ]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();

    // Disabled type checker because react-router-dom defines a custom type for what is considered
    // JSON-serializable, which does not include Date objects. This doesn't actually affect anything,
    // the type checker just complains.
    // eslint-disable-next-line
    // @ts-ignore
    submit(form, { action: 'cancel', method: 'POST', encType: 'application/json' });
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} size="2xl">
      <DialogTitle>Cancel policy</DialogTitle>
      <DialogDescription>
        Marks the policy as canceled and tracks certain information about the cancellation.
      </DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
          <Fieldset>
            <FieldGroup>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field>
                  <Label>Cancellation Type</Label>
                  <Select
                    value={form.type || ''}
                    onChange={(e) => {
                      const type = e.currentTarget.value;
                      updateForm(['type'], (prev) => ({
                        ...prev,
                        type: type as BusinessPolicyCancellationType
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['type'])}
                  >
                    <option value={BusinessPolicyCancellationType.Cancellation}>
                      Cancellation
                    </option>
                    <option value={BusinessPolicyCancellationType.Nonrenewal}>Non-Renewal</option>
                    <option value={BusinessPolicyCancellationType.QuoteExpired}>
                      Quote Expired
                    </option>
                  </Select>
                  {getValidationError(validationErrors, ['type']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['type'])}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Cancellation Reason</Label>
                  <Select
                    value={form.reason || ''}
                    onChange={(e) => {
                      const reason = e.currentTarget.value;
                      updateForm(['reason'], (prev) => ({
                        ...prev,
                        reason: reason as BusinessPolicyCancellationReason
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['reason'])}
                  >
                    {Object.values(BusinessPolicyCancellationReason).map((reason) => (
                      <option key={reason} value={reason}>
                        {titleCase(reason)}
                      </option>
                    ))}
                  </Select>
                  {getValidationError(validationErrors, ['reason']) && (
                    <ErrorMessage>{getValidationError(validationErrors, ['reason'])}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Canceled By</Label>
                  <Select
                    value={form.canceledBy || ''}
                    onChange={(e) => {
                      const canceledBy = e.currentTarget.value;
                      updateForm(['canceledBy'], (prev) => ({
                        ...prev,
                        canceledBy: canceledBy as BusinessPolicyCancellationSource
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['canceledBy'])}
                  >
                    <option value={BusinessPolicyCancellationSource.Carrier}>Carrier</option>
                    <option value={BusinessPolicyCancellationSource.Insured}>Insured</option>
                    <option value={BusinessPolicyCancellationSource.Agency}>Agency</option>
                  </Select>
                  {getValidationError(validationErrors, ['quoteNumber']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['quoteNumber'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={
                      form.effectiveAt ? new Date(form.effectiveAt).toISOString().split('T')[0] : ''
                    }
                    onChange={(e) => {
                      const effectiveAt = e.currentTarget.value;
                      updateForm(['effectiveAt'], (prev) => ({
                        ...prev,
                        effectiveAt:
                          effectiveAt && !isNaN(new Date(effectiveAt).getTime())
                            ? new Date(effectiveAt)
                            : prev.effectiveAt
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['effectiveAt'])}
                  />
                  {getValidationError(validationErrors, ['effectiveAt']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['effectiveAt'])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>

            <FieldGroup>
              <div className="grid xs:grid-cols-2 sm:grid-cols-3 gap-4">
                <Field>
                  <Label>Return Premium</Label>
                  <CurrencyInput
                    value={form.returnPremium ?? undefined}
                    onChange={(returnPremium) => {
                      updateForm(['returnPremium'], (prev) => ({
                        ...prev,
                        returnPremium: isNaN(returnPremium) ? prev.returnPremium : returnPremium
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['returnPremium'])}
                  />
                  {getValidationError(validationErrors, ['returnPremium']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['returnPremium'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Return Commission</Label>
                  <CurrencyInput
                    value={form.returnCommission ?? undefined}
                    onChange={(returnCommission) => {
                      updateForm(['returnCommission'], (prev) => ({
                        ...prev,
                        returnCommission: isNaN(returnCommission)
                          ? prev.returnCommission
                          : returnCommission
                      }));
                    }}
                    invalid={!!getValidationError(validationErrors, ['returnCommission'])}
                  />
                  {getValidationError(validationErrors, ['returnCommission']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['returnCommission'])}
                    </ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
            <FieldGroup>
              <Field>
                <Label>Details</Label>
                <Description>Any additional details about the cancellation.</Description>
                <Textarea
                  value={form.reasonDescription || ''}
                  onChange={(e) => {
                    const reasonDescription = e.currentTarget.value;
                    updateForm(['reasonDescription'], (prev) => ({
                      ...prev,
                      reasonDescription
                    }));
                  }}
                  rows={3}
                  resizable={false}
                  invalid={!!getValidationError(validationErrors, ['reasonDescription'])}
                />
                {getValidationError(validationErrors, ['reasonDescription']) && (
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['reasonDescription'])}
                  </ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          <Button
            type="submit"
            color="sky"
            disabled={!form}
            loading={navigation.state === 'submitting'}
          >
            Cancel Policy
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export const PolicyOverviewPage = () => {
  const loader = useRouteLoaderData('policies/:id') as
    | {
        data: Promise<{
          businessPolicy?: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ businessPolicy }) => {
          if (!businessPolicy) {
            return null;
          }

          const policyStats = [
            {
              name: 'Premium',
              value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
                businessPolicy.pricing.premium
              )
            },
            {
              name: 'Status',
              value: titleCase(businessPolicy.state)
            },
            {
              name: 'Billing Mode',
              value: titleCase(businessPolicy.pricing.billingType)
            }
          ];

          return (
            <div className="space-y-6">
              {!!businessPolicy.cancellation &&
                !businessPolicy.reinstatement &&
                businessPolicy.state !== PolicyState.Canceled && (
                  <div className="rounded-lg bg-orange-100 p-4 border border-orange-200/80">
                    <div className="flex">
                      <div className="shrink-0">
                        <ExclamationTriangleIcon
                          aria-hidden="true"
                          className="size-5 text-orange-800"
                        />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-orange-800">
                          Cancellation Pending
                        </div>
                        <div className="mt-2 text-sm text-orange-800">
                          This policy has been marked as canceled and its state will automatically
                          update after the effective date passes.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              {!!businessPolicy.renewal?.nextPolicies?.length && (
                <div className="rounded-lg bg-orange-100 p-4 border border-orange-200/80">
                  <div className="flex">
                    <div className="shrink-0">
                      <ExclamationTriangleIcon
                        aria-hidden="true"
                        className="size-5 text-orange-800"
                      />
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-orange-800">
                        Newer policy version available
                      </div>
                      <div className="mt-2 text-sm text-orange-800">
                        You're viewing an older version of this policy. This policy has been
                        renewed.
                      </div>
                    </div>
                  </div>
                  <Button
                    plain
                    href={`/app/policies/${businessPolicy.renewal.nextPolicies[0].id}/overview`}
                    className="ml-5 mt-2 text-sm text-orange-800"
                  >
                    View Newer Policy &rarr;
                  </Button>
                </div>
              )}
              <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
                <div className="px-4 py-5 sm:px-6">
                  <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
                    <div>
                      <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900">
                        Policy information
                      </h3>
                      <p className="text-sm text-neutral-500">
                        Basic information about this policy.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="@container">
                  <dl className="px-4 py-5 sm:px-6 mx-auto grid grid-cols-1 gap-8 @sm:grid-cols-2 @lg:grid-cols-3">
                    {policyStats.map((stat) => (
                      <div
                        key={stat.name}
                        className="flex flex-wrap items-baseline justify-between bg-white"
                      >
                        <dt className="text-sm font-medium text-neutral-500">{stat.name}</dt>
                        <dd className="w-full flex-none text-xl font-medium leading-10 tracking-tight text-neutral-900">
                          {stat.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <dl className="divide-y divide-neutral-100 border-t border-neutral-100 pb-2">
                    {!!businessPolicy.cancellation && !businessPolicy.reinstatement && (
                      <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-neutral-500">
                          Cancellation Effective Date
                        </dt>
                        <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                          {businessPolicy.cancellation.effectiveAt && (
                            <>
                              {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                                new Date(businessPolicy.cancellation.effectiveAt)
                              )}
                            </>
                          )}
                          {!businessPolicy.expiresAt && 'N/A'}
                        </dd>
                      </div>
                    )}
                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-neutral-500">Expiration Date</dt>
                      <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                        {businessPolicy.expiresAt && (
                          <>
                            {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                              new Date(businessPolicy.expiresAt)
                            )}
                          </>
                        )}
                        {!businessPolicy.expiresAt && 'N/A'}
                      </dd>
                    </div>
                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-neutral-500">Carrier</dt>
                      <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                        {businessPolicy.carrier.name}
                      </dd>
                    </div>
                    <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-neutral-500">Wholesale Broker</dt>
                      <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                        {businessPolicy.broker?.name || 'N/A'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
            </div>
          );
        }}
      </Await>
    </React.Suspense>
  );
};

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

export const PolicyDocumentsPage = () => {
  const navigation = useNavigation();
  const submit = useSubmit();
  const outlet = useOutlet();
  // const location = useLocation();
  const action = useActionData() as ActionResult<DeleteFileMutation>;
  const loader = useRouteLoaderData('policies/:id') as
    | {
        data: Promise<{
          businessPolicy?: BusinessPolicyFieldsFragment & BusinessPolicyFileFieldsFragment;
        }>;
      }
    | undefined;

  const [deleteFileId, setDeleteFileId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (
      navigation.state === 'loading' &&
      navigation.formAction === navigation.location.pathname &&
      !!action?.data?.deleteFile?.id
    ) {
      setDeleteFileId(null);
    }
  }, [navigation.state, action?.data?.deleteFile?.id]);

  const onDelete = () => {
    submit({ fileId: deleteFileId }, { method: 'DELETE', encType: 'application/json' });
  };

  return (
    <React.Suspense>
      <Await resolve={loader?.data}>
        {({ businessPolicy }) => (
          <>
            <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
              <div className="px-4 py-5 sm:px-6">
                <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
                  <div>
                    <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900">
                      Documents
                    </h3>
                    <p className="text-sm text-neutral-500">
                      All documents associated with this policy.
                    </p>
                  </div>
                  {!!businessPolicy?.files?.length && (
                    <div className="flex-shrink-0 mt-2 sm:mt-0">
                      <Button
                        href={`/app/policies/${businessPolicy?.id}/documents/new`}
                        type="button"
                        color="sky"
                      >
                        Upload Document
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-4 sm:px-6">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  {!businessPolicy?.files?.length && (
                    <div className="text-center p-8">
                      <DocumentPlusIcon className="mx-auto h-12 w-12 text-neutral-400" />
                      <h4 className="mt-2 text-sm font-semibold text-neutral-900">No documents</h4>
                      <p className="mt-1 text-sm text-neutral-500">
                        Get started by uploading a document.
                      </p>
                      <div className="mt-6">
                        <Button
                          href={`/app/policies/${businessPolicy?.id}/documents/new`}
                          type="button"
                          color="sky"
                        >
                          Upload Document
                        </Button>
                      </div>
                    </div>
                  )}
                  {!!businessPolicy?.files?.length && (
                    <div className="inline-block min-w-full py-2 align-middle">
                      <table className="min-w-full divide-y divide-neutral-200">
                        <thead className="bg-neutral-50">
                          <tr>
                            <th
                              scope="col"
                              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-neutral-900 sm:pl-6 lg:pl-8"
                            >
                              Name
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900"
                            >
                              Type
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900"
                            >
                              Upload Date
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-3.5 text-left text-sm font-semibold text-neutral-900"
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-200 bg-white">
                          {businessPolicy?.files.map((file) => (
                            <tr key={file.id}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6 lg:pl-8">
                                <div className="text-sm font-medium text-neutral-900">
                                  {file.name}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">
                                <div
                                  className={clsx(
                                    'text-neutral-600 bg-neutral-50 ring-neutral-500/10',
                                    'inline-flex rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset'
                                  )}
                                >
                                  {titleCase(file.type)}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-neutral-500">
                                {!file.createdAt || isNaN(new Date(file.createdAt).getTime())
                                  ? 'N/A'
                                  : new Intl.DateTimeFormat('en-US').format(
                                      new Date(file.createdAt)
                                    )}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 sm:pr-6 lg:pr-8">
                                <div className="flex">
                                  <Button
                                    type="button"
                                    target="_blank"
                                    plain
                                    href={file.url || '#'}
                                    className="rounded-full p-1"
                                  >
                                    <EyeIcon className="size-8 text-neutral-400" />
                                  </Button>
                                  <Button
                                    type="button"
                                    plain
                                    className="rounded-full p-1 cursor-pointer"
                                    onClick={() => setDeleteFileId(file.id)}
                                  >
                                    <TrashIcon className="size-8 text-neutral-400" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {outlet && React.cloneElement(outlet, {})}
            </AnimatePresence>

            <Dialog open={!!deleteFileId} onClose={() => setDeleteFileId(null)} size="md">
              <DialogTitle>Delete Document</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete{' '}
                <span className="font-semibold">
                  {businessPolicy?.files?.find((f) => f.id === deleteFileId)?.name}
                </span>
                ? This action is permanent and the file will no longer be visible to the client (if
                applicable).
              </DialogDescription>
              <DialogActions>
                <Button
                  type="button"
                  color="white"
                  onClick={() => setDeleteFileId(null)}
                  disabled={navigation.state === 'submitting'}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  color="red"
                  onClick={onDelete}
                  loading={navigation.state === 'submitting'}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Await>
    </React.Suspense>
  );
};

export const PolicyDocumentUploadPage = () => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<UploadPolicyFileMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<DeepPartial<UploadFileInput>>({});
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  const visibleTypes = [
    FileType.Binder,
    FileType.Endorsement,
    FileType.LostPolicyRelease,
    FileType.PolicyDocument,
    FileType.QuoteDocument
  ];

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (
      navigation.state === 'loading' &&
      navigation.formAction === navigation.location.pathname &&
      !!action?.data?.uploadPolicyFile?.id
    ) {
      navigate(-1);
    }
  }, [navigation.state, action?.data?.uploadPolicyFile?.id]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();

    // Construct a multi-part form data request with the file
    const formData = new FormData();
    form.file && formData.append('file', form.file);
    form.type && formData.append('type', form.type);
    formData.append('notes', form.notes || '');
    formData.append('visible', form.visible ? 'true' : 'false');

    submit(formData, { method: 'POST', encType: 'multipart/form-data' });
  };

  return (
    <MountableDialog onClose={() => navigate(-1)} size="xl">
      <DialogTitle>Upload a new document</DialogTitle>
      <DialogDescription>Upload a new document to associate with this policy.</DialogDescription>
      <Form onSubmit={handleSubmit}>
        <DialogBody className="space-y-6 [&>*+*]:pt-6 divide-y divide-neutral-200">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Document Type</Label>
                <Combobox
                  value={form.type || undefined}
                  minQueryLength={0}
                  maxOptions={1000}
                  onChange={(type) =>
                    updateForm(['type'], (prev) => ({
                      ...prev,
                      type: type ? (type as FileType) : undefined,
                      visible: type ? visibleTypes.includes(type) : undefined
                    }))
                  }
                  options={Object.values(FileType).map((type) => ({
                    value: type,
                    label: titleCase(type),
                    badgeText: visibleTypes.includes(type) ? 'Visible to client' : undefined
                  }))}
                  error={getValidationError(validationErrors, ['type'])}
                />
              </Field>
              <Field>
                <Label>Notes</Label>
                <Description>These notes will not be visible to the client.</Description>
                <Textarea
                  value={form.notes || ''}
                  rows={3}
                  onChange={(e) => {
                    const notes = e.currentTarget.value;
                    updateForm(['notes'], (prev) => ({ ...prev, notes }));
                  }}
                  invalid={!!getValidationError(validationErrors, ['notes'])}
                />
                {getValidationError(validationErrors, ['notes']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['notes'])}</ErrorMessage>
                )}
              </Field>
              <Field>
                <Label>File</Label>
                <Description>
                  Drag and drop or click to select files from your computer. Only one file can be
                  uploaded at a time.
                </Description>
                <FileInput
                  onChange={(files) =>
                    updateForm(['file'], (prev) => ({ ...prev, file: files[0] }))
                  }
                  invalid={!!getValidationError(validationErrors, ['file'])}
                />
                {getValidationError(validationErrors, ['file']) && (
                  <ErrorMessage>{getValidationError(validationErrors, ['file'])}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button
            type="button"
            color="white"
            disabled={navigation.state === 'submitting'}
            onClick={() => navigate(-1)}
          >
            Close
          </Button>
          <Button
            type="submit"
            color="sky"
            disabled={!form}
            loading={navigation.state === 'submitting'}
          >
            Upload Document
          </Button>
        </DialogActions>
      </Form>
    </MountableDialog>
  );
};

export const PolicyHistoryPage = () => {
  return (
    <div>
      <ul role="list" className="space-y-6">
        {activity.map((activityItem, activityItemIdx) => (
          <li key={activityItem.id} className="relative flex gap-x-4">
            <div
              className={clsx(
                activityItemIdx === activity.length - 1 ? 'h-6' : '-bottom-6',
                'absolute left-0 top-0 flex w-6 justify-center'
              )}
            >
              <div className="w-px bg-neutral-200" />
            </div>

            <div className="relative flex size-6 flex-none items-center justify-center">
              <div className="size-1.5 rounded-full bg-neutral-100 ring-1 ring-neutral-300" />
            </div>
            <p className="flex-auto py-0.5 text-xs/5 text-neutral-500">
              <span className="font-medium text-neutral-900">{activityItem.person.name}</span>{' '}
              {activityItem.type} the policy.
            </p>
            <time
              dateTime={activityItem.dateTime}
              className="flex-none py-0.5 text-xs/5 text-neutral-500"
            >
              {activityItem.date}
            </time>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const PolicyExplorerPage = () => {
  const loader = useRouteLoaderData('policies/:id') as
    | {
        data: Promise<{
          businessPolicy: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <React.Suspense fallback={<Spinner size="size-8" color="neutral" />}>
      <Await resolve={loader?.data}>
        {({ businessPolicy }) => {
          if (!businessPolicy?.details) {
            return (
              <div className="text-center py-12">
                <CubeTransparentIcon className="mx-auto size-12 text-neutral-400" />
                <h3 className="mt-2 text-sm font-semibold text-neutral-900">Policy Explorer</h3>
                <p className="mt-1 text-sm text-neutral-500 max-w-md mx-auto">
                  Explore this policy with AI. To start, upload a document. The policy explorer can
                  take a few minutes to become available.
                </p>
              </div>
            );
          }

          return <PolicyExplorer policy={businessPolicy} />;
        }}
      </Await>
    </React.Suspense>
  );
};

const PolicyExplorer = (props: {
  policy: BusinessPolicyFieldsFragment & BusinessPolicyDetailFieldsFragment;
}) => {
  const selectedPolicy = props.policy;
  const [showPolicyQuotes, setShowPolicyQuotes] = React.useState<Record<string, boolean>>({});

  const policyStats = [
    {
      name: 'Premium',
      value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        selectedPolicy.pricing.premium || 0
      )
    },
    {
      name: 'Policy Number',
      value: selectedPolicy.policyNumber || selectedPolicy.details?.policyNumber || 'N/A'
    },
    {
      name: 'Expiration',
      value: new Date(selectedPolicy.expiresAt || '').toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })
    }
  ];

  const policyLimits = [
    { name: 'aggregate', limit: selectedPolicy.details?.limits?.cyberAggregateLimit },
    { name: 'retention', limit: selectedPolicy.details?.limits?.cyberRetentionLimit },
    { name: 'aggregate', limit: selectedPolicy.details?.limits?.glAggregateLimit },
    {
      name: 'medical per occurrence',
      limit: selectedPolicy.details?.limits?.glMedicalPerOccurrenceLimit
    },
    {
      name: 'medical per person',
      limit: selectedPolicy.details?.limits?.glMedicalPerPersonLimit
    },
    { name: 'per occurrence', limit: selectedPolicy.details?.limits?.glPerOccurrenceLimit },
    {
      name: 'personal and advertising injury',
      limit: selectedPolicy.details?.limits?.glPersonalAndAdvertisingInjuryLimit
    },
    {
      name: 'products and completed operations',
      limit: selectedPolicy.details?.limits?.glProductsAndCompletedOperationsLimit
    },
    {
      name: 'rented premises damage',
      limit: selectedPolicy.details?.limits?.glRentedPremisesDamageLimit
    },
    { name: 'building', limit: selectedPolicy.details?.limits?.propertyBuildingLimit },
    { name: 'contents', limit: selectedPolicy.details?.limits?.propertyContentsLimit },
    { name: 'per accident', limit: selectedPolicy.details?.limits?.wcPerAccidentLimit },
    {
      name: 'per disease (employee)',
      limit: selectedPolicy.details?.limits?.wcPerDiseaseEmployeeLimit
    },
    {
      name: 'per disease (policy)',
      limit: selectedPolicy.details?.limits?.wcPerDiseasePolicyLimit
    },
    ...(selectedPolicy.details?.extraLimits || []),
    ...(selectedPolicy.details?.locations?.flatMap((location) => [
      {
        name: `building (${location.address.line1})`,
        limit: location.limits?.propertyBuildingLimit
      },
      {
        name: `contents (${location.address.line1})`,
        limit: location.limits?.propertyContentsLimit
      }
    ]) || [])
  ].filter((limit) => !!limit?.limit);

  return (
    <>
      <div className="mt-8 w-full grow xl:flex">
        <div className="flex-1 space-y-8 pb-8 w-full xl:pb-0 xl:pr-4 xl:flex-1">
          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
                <div>
                  <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900">
                    Policy overview
                  </h3>
                  <p className="text-sm text-neutral-500">Basic details about this policy.</p>
                </div>
              </div>
            </div>
            <div className="@container">
              <dl className="px-4 py-5 sm:px-6 mx-auto grid grid-cols-1 gap-8 @sm:grid-cols-2 @lg:grid-cols-3">
                {policyStats.map((stat) => (
                  <div
                    key={stat.name}
                    className="flex flex-wrap items-baseline justify-between bg-white"
                  >
                    <dt className="text-sm font-medium text-neutral-500">{stat.name}</dt>
                    <dd className="w-full flex-none text-xl font-medium leading-10 tracking-tight text-neutral-900">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
              <dl className="divide-y divide-neutral-100 border-t border-neutral-100 pb-2">
                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Insured Entity</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                    <span className="font-medium">
                      {selectedPolicy.details?.insured?.name || 'Unknown'}
                    </span>
                    <br />
                    <span>{selectedPolicy.details?.insured?.address?.line1 || 'Unknown'}</span>
                    <br />
                    <span>
                      {selectedPolicy.details?.insured?.address?.city || 'Unknown'},{' '}
                      {selectedPolicy.details?.insured?.address?.zone || 'Unknown'}{' '}
                      {selectedPolicy.details?.insured?.address?.postalCode || 'Unknown'}
                    </span>
                  </dd>
                </div>
                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Carrier</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                    <span className="font-medium">
                      {selectedPolicy.details?.carrier?.name || 'Unknown'}
                    </span>
                    <br />
                    <span>{selectedPolicy.details?.carrier?.address?.line1 || 'Unknown'}</span>
                    <br />
                    <span>
                      {selectedPolicy.details?.carrier?.address?.city || 'Unknown'},{' '}
                      {selectedPolicy.details?.carrier?.address?.zone || 'Unknown'}{' '}
                      {selectedPolicy.details?.carrier?.address?.postalCode || 'Unknown'}
                    </span>
                  </dd>
                </div>
                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Policy Summary</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                    {selectedPolicy.details?.summary || 'Unknown'}
                  </dd>
                </div>
                <div className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-neutral-500">Limits</dt>
                  <dd className="mt-1 text-sm leading-6 text-neutral-900 sm:col-span-2 sm:mt-0">
                    {policyLimits.map((limit) => (
                      <div key={limit.name}>
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 2,
                          currency: 'USD'
                        }).format(limit.limit || 0)}{' '}
                        {limit.name}
                      </div>
                    ))}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
                <div>
                  <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900">
                    What's covered
                  </h3>
                  <p className="text-sm text-neutral-500">Coverage included on this policy.</p>
                </div>
              </div>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-col gap-2">
                <dl className="grid grid-cols-1 gap-4">
                  {selectedPolicy.details?.coverageDescriptions?.map((coverage) => (
                    <div key={coverage.title}>
                      <dt className="text-sm flex items-center gap-2">
                        <CheckCircleIcon className="h-4 w-4 text-emerald-500" aria-hidden="true" />
                        <span className="font-medium text-neutral-500">{coverage.title}</span>
                      </dt>
                      <dd className="text-sm text-neutral-900 ml-6 mt-1 leading-5">
                        {coverage.description}
                      </dd>
                      {coverage.policyQuotes.length > 0 && !showPolicyQuotes[coverage.title] && (
                        <Headless.Button
                          onClick={() =>
                            setShowPolicyQuotes({
                              ...showPolicyQuotes,
                              [coverage.title]: true
                            })
                          }
                          className="text-xs font-medium text-primary-500 hover:text-primary-800 inline-flex items-center gap-1 ml-6"
                        >
                          Show relevant quotes <ArrowRightIcon className="h-3 w-3" />
                        </Headless.Button>
                      )}
                      {coverage.policyQuotes.length > 0 && showPolicyQuotes[coverage.title] && (
                        <div className="flex flex-col gap-2 mt-2 ml-6">
                          {coverage.policyQuotes.map((quote) => (
                            <div
                              key={quote}
                              className="text-xs text-neutral-500 border-l-4 border-neutral-200 px-2 py-0.5"
                            >
                              {quote}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-neutral-400 mt-2">
                  The coverage descriptions provided here may not be complete, and may have
                  important exclusions or limitations. Please read the full policy document to
                  confirm the information here.
                </p>
              </div>
            </div>
          </div>

          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
                <div>
                  <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900">
                    What's not covered
                  </h3>
                  <p className="text-sm text-neutral-500">
                    Exclusions and other limitations on this policy.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-col gap-2">
                <dl className="grid grid-cols-1 gap-4">
                  {selectedPolicy.details?.exclusionDescriptions?.map((exclusion) => (
                    <div key={exclusion.title}>
                      <dt className="text-sm flex items-center gap-2">
                        <XCircleIcon className="h-4 w-4 text-red-500" aria-hidden="true" />
                        <span className="font-medium text-neutral-500">{exclusion.title}</span>
                      </dt>
                      <dd className="text-sm text-neutral-900 ml-6 mt-1 leading-5">
                        {exclusion.description}
                      </dd>
                      {exclusion.policyQuotes.length > 0 && !showPolicyQuotes[exclusion.title] && (
                        <Headless.Button
                          onClick={() =>
                            setShowPolicyQuotes({
                              ...showPolicyQuotes,
                              [exclusion.title]: true
                            })
                          }
                          className="text-xs font-medium text-primary-500 hover:text-primary-800 inline-flex items-center gap-1 ml-6"
                        >
                          Show relevant quotes <ArrowRightIcon className="h-3 w-3" />
                        </Headless.Button>
                      )}
                      {exclusion.policyQuotes.length > 0 && showPolicyQuotes[exclusion.title] && (
                        <div className="flex flex-col gap-2 mt-2 ml-6">
                          {exclusion.policyQuotes.map((quote) => (
                            <div
                              key={quote}
                              className="text-xs text-neutral-500 border-l-4 border-neutral-200 px-2 py-0.5"
                            >
                              {quote}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </dl>
                <p className="text-xs text-neutral-400 mt-2">
                  The exclusions provided here may not be complete, and may have important caveats
                  or conditions. Please read the full policy document to confirm the information
                  here.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="shrink-0 border-neutral-200 w-full space-y-8 xl:pl-4 xl:w-[25rem]">
          <div className="divide-y divide-neutral-200 overflow-hidden rounded-lg bg-white shadow">
            <div className="px-4 py-5 sm:px-6">
              <div className="flex flex-wrap gap-2 items-center justify-between sm:flex-nowrap sm:gap-4">
                <div>
                  <h3 className="text-base/4 font-sans font-semibold leading-6 m-0 text-neutral-900">
                    Ask a question
                  </h3>
                  <p className="text-sm text-neutral-500">
                    We'll find related information in this policy.
                  </p>
                </div>
              </div>
            </div>
            <div className="px-4 py-5 sm:px-6">
              <PolicyExplorerChat />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const PolicyExplorerChat = () => {
  const loader = useLoaderData() as
    | {
        data: Promise<{
          policyExplorerConversation?: PolicyExplorerConversationMessageFieldsFragment[];
        }>;
      }
    | undefined;

  return (
    <React.Suspense fallback={<PolicyChatFallback />}>
      <Await resolve={loader?.data}>
        {(data) => <PolicyExplorerChatInner messages={data?.policyExplorerConversation || []} />}
      </Await>
    </React.Suspense>
  );
};

const PolicyChatFallback = () => (
  <div className="space-y-4 animate-pulse">
    <div className="flex justify-start">
      <div className="rounded-lg p-3 w-full max-w-[80%] bg-neutral-100">
        <div className="h-3 w-3/4 bg-neutral-300 rounded-full" />
        <div className="h-3 w-1/2 bg-neutral-300 rounded-full mt-2" />
        <div className="h-3 w-5/6 bg-neutral-300 rounded-full mt-2" />
      </div>
    </div>
    <div className="flex justify-end">
      <div className="rounded-lg p-3 w-full max-w-[75%] bg-primary-100">
        <div className="h-3 w-3/4 bg-primary-300 rounded-full" />
        <div className="h-3 w-1/2 bg-primary-300 rounded-full mt-2" />
      </div>
    </div>
    <div className="flex justify-start">
      <div className="rounded-lg p-3 w-full max-w-[80%] bg-neutral-100">
        <div className="h-3 w-3/4 bg-neutral-300 rounded-full" />
        <div className="h-3 w-2/3 bg-neutral-300 rounded-full mt-2" />
        <div className="h-3 w-5/6 bg-neutral-300 rounded-full mt-2" />
        <div className="h-3 w-1/2 bg-neutral-300 rounded-full mt-2" />
        <div className="h-3 w-3/4 bg-neutral-300 rounded-full mt-2" />
      </div>
    </div>
    <div className="flex">
      <div className="rounded-lg w-full h-32 border border-neutral-300" />
    </div>
  </div>
);

const PolicyExplorerChatInner = (props: {
  messages: PolicyExplorerConversationMessageFieldsFragment[];
}) => {
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<AddPolicyExplorerConversationMessageMutation>;

  // State for the chat
  const [editorText, setEditorText] = React.useState('');
  const [stagedMessage, setStagedMessage] = React.useState('');
  const [messages, setMessages] = React.useState<PolicyExplorerConversationMessageFieldsFragment[]>(
    props.messages
  );
  const [showAttachments, setShowAttachments] = React.useState<Record<string, boolean>>({});

  React.useEffect(() => {
    setMessages(props.messages);
  }, [props.messages]);

  React.useEffect(() => {
    if (action?.data?.addPolicyExplorerConversationMessage?.length) {
      setMessages(action.data.addPolicyExplorerConversationMessage);
    }
  }, [action?.data?.addPolicyExplorerConversationMessage]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setEditorText('');
    setStagedMessage(editorText);
    submit({ message: editorText }, { method: 'POST', encType: 'application/json' });
  };

  return (
    <div className="flex flex-col space-y-5">
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          <div className="flex justify-start">
            <div className="rounded-lg p-3 bg-neutral-100 text-neutral-900 max-w-[75%] xl:max-w-full">
              <pre className="font-body text-sm whitespace-pre-wrap">
                Ask a question about this policy.
              </pre>
            </div>
          </div>
          {messages.map((message) => (
            <div
              key={message.id}
              className={clsx(
                'flex',
                message.role === PolicyExplorerConversationMessageRole.User
                  ? 'justify-end'
                  : 'justify-start'
              )}
            >
              <div
                className={clsx(
                  'rounded-lg p-3 flex flex-col max-w-[75%] xl:max-w-full',
                  message.role === PolicyExplorerConversationMessageRole.User
                    ? 'bg-sky-500 text-white'
                    : 'bg-neutral-100 text-neutral-900'
                )}
              >
                <pre className="font-body text-sm whitespace-pre-wrap">{message.content}</pre>
                {message.attachments.length > 0 && !showAttachments[message.id] && (
                  <Headless.Button
                    onClick={() =>
                      setShowAttachments({
                        ...showAttachments,
                        [message.id]: true
                      })
                    }
                    className="text-xs font-medium text-primary-500 hover:text-primary-800 inline-flex items-center gap-1 mt-2"
                  >
                    Show relevant quotes <ArrowRightIcon className="h-3 w-3" />
                  </Headless.Button>
                )}
                {message.attachments.length > 0 && showAttachments[message.id] && (
                  <div className="flex flex-col gap-2 mt-4">
                    {message.attachments?.map((attachment) => (
                      <div
                        key={attachment.content}
                        className="text-xs border-l-4 border-neutral-200 px-2 py-0.5"
                      >
                        {attachment.content}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {navigation.state === 'submitting' && (
            <>
              <div className="flex justify-end">
                <div className="rounded-lg p-3 bg-sky-500 text-white">
                  <pre className="font-body text-sm whitespace-pre-wrap">{stagedMessage}</pre>
                </div>
              </div>
              <AIChatProgress />
            </>
          )}
          <div className="mt-4 text-center">
            <p className="text-xs text-neutral-400">
              Please note: This assistant will attempt to find information in your policy. However,
              it is not a licensed agent. Always check your policy documents or schedule a call with
              an Oyster agent for the most accurate information.
            </p>
          </div>
        </div>
      </div>
      <div className="mt-4">
        <form onSubmit={handleSubmit} className="relative">
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label className="sr-only">Ask a question or followup</Label>
                <Textarea
                  rows={4}
                  placeholder="Ask a question or followup..."
                  resizable={false}
                  disabled={navigation.state === 'submitting'}
                  value={editorText}
                  onChange={(e) => setEditorText(e.target.value)}
                />
                {action?.applicationError && (
                  <ErrorMessage>{action.applicationError.message}</ErrorMessage>
                )}
              </Field>
            </FieldGroup>
          </Fieldset>

          <Button
            type="submit"
            color="sky"
            className="mt-4 justify-end"
            loading={navigation.state === 'submitting'}
          >
            Send Message
          </Button>
        </form>
      </div>
    </div>
  );
};

const AIChatProgress = () => {
  // Build a two-minute animation to keep the user engaged while the quote loads
  const steps = [
    { title: 'Loading policy documents...', maxDuration: 2000, maxWidth: 10 },
    { title: 'Reading carefully...', maxDuration: 6000, maxWidth: 45 },
    { title: 'Thinking...', maxDuration: 5000, maxWidth: 80 },
    { title: 'Thinking...', maxDuration: 8000, maxWidth: 90 },
    { title: 'Thinking...', maxDuration: 13000, maxWidth: 100 }
  ];

  const [stepIndex, setStepIndex] = React.useState(0);
  React.useEffect(() => {
    if (stepIndex === steps.length) {
      return;
    }

    const timeout = setTimeout(() => {
      setStepIndex((prev) => (prev >= steps.length - 1 ? prev : prev + 1));
    }, steps[stepIndex].maxDuration);

    return () => clearTimeout(timeout);
  }, [stepIndex]);

  return (
    <div className="flex justify-start">
      <div className="rounded-lg p-3 w-full max-w-[80%] bg-neutral-100">
        <div className="h-3 w-3/4 bg-neutral-300 rounded-full animate-pulse" />
        <div className="h-3 w-2/3 bg-neutral-300 rounded-full mt-2 animate-pulse" />
        <div className="h-3 w-5/6 bg-neutral-300 rounded-full mt-2 animate-pulse" />

        <div className="mt-4 text-xs font-medium text-primary-600 dark:text-primary-400">
          {steps[stepIndex].title}
        </div>
        <div className="mt-1">
          <div className="h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-700">
            <div
              className={`h-2 rounded-full bg-gradient-to-r to-primary-300 from-primary-600 bg-[size:200%_100%] animate-progress-bar transition-all ease-in-out duration-1000`}
              style={{ width: `${steps[stepIndex].maxWidth}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
